import { Component, inject, signal, OnInit, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { DemandesService } from '../../../../core/service/demandes-service/demandes.service';
import { AuthService } from '../../../../core/service/auth/auth.service';
import { ApiService } from '../../../../core/service/api-service/api-service';
import { API } from '../../../../core/API';
import { SwalUtils } from '../../../../shared/utils/swal.utils';
import { PermissionService } from '../../../../core/service/permission/permission.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-demande-detail',
  standalone: false,
  templateUrl: './demande-detail.component.html',
})
export class DemandeDetailComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private demandesService = inject(DemandesService);
  private authService = inject(AuthService);
  private api = inject(ApiService);
  private permService = inject(PermissionService);
  private destroyRef = inject(DestroyRef);

  demande = signal<any>(null);
  isLoading = signal(true);
  isActing = signal(false);
  motif = signal('');
  showMotifInput = signal(false);
  pendingAction = signal<'validate' | 'reject' | 'cancel' | 'club-accept' | 'club-reject' | null>(null);

  entityType = signal('');
  demandeId = signal(0);

  get user() { return this.authService.getUser(); }

  ngOnInit(): void {
    const entityType = this.route.snapshot.paramMap.get('entity_type') || '';
    const id = parseInt(this.route.snapshot.paramMap.get('id') || '0', 10);
    this.entityType.set(entityType);
    this.demandeId.set(id);
    this.loadDemande();
  }

  loadDemande(): void {
    this.isLoading.set(true);
    this.demandesService.getDetail(this.entityType(), this.demandeId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.demande.set(res.success ? (res.data || null) : null);
          this.isLoading.set(false);
        },
        error: () => this.isLoading.set(false),
      });
  }

  get canCancel(): boolean {
    if (!this.permService.can('licence', 'demandes', 'cancel')) return false;
    const d = this.demande();
    if (!d || d.demande_status !== 'PENDING') return false;
    const u = this.user;
    return u?.user_object_id === d.club_id;
  }

  get canClubAction(): boolean {
    if (!this.permService.can('licence', 'demandes', 'club_action')) return false;
    const d = this.demande();
    if (!d || d.demande_type !== 'TRANSFER' || d.demande_status !== 'PENDING') return false;
    const u = this.user;
    return u?.user_object_id === d.source_club_id;
  }

  get canFedAction(): boolean {
    if (!this.permService.can('licence', 'demandes', 'validate')) return false;
    const d = this.demande();
    if (!d) return false;
    const u = this.user;
    if (!u?.roles?.includes('FED_ADMIN') && !u?.roles?.includes('SUPER_ADMIN')) return false;
    if (d.demande_type === 'TRANSFER') return d.demande_status === 'CLUB_VALIDATED';
    return d.demande_status === 'PENDING';
  }

  get showLicenceGeneratedInfo(): boolean {
    return this.demande()?.demande_status === 'FED_VALIDATED';
  }

  startAction(action: 'validate' | 'reject' | 'cancel' | 'club-accept' | 'club-reject'): void {
    this.pendingAction.set(action);
    this.motif.set('');
    this.showMotifInput.set(action === 'reject' || action === 'club-reject');
  }

  cancelAction(): void {
    this.pendingAction.set(null);
    this.showMotifInput.set(false);
    this.motif.set('');
  }

  async confirmAction(): Promise<void> {
    const action = this.pendingAction();
    if (!action || this.isActing()) return;

    const motif = this.motif();
    if ((action === 'reject' || action === 'club-reject') && !motif.trim()) {
      SwalUtils.error('Champ requis', 'Le motif est obligatoire pour rejeter une demande');
      return;
    }

    this.isActing.set(true);
    const demandeId = this.demandeId();
    const entityType = this.entityType();

    let obs$;
    switch (action) {
      case 'validate':
        obs$ = this.demandesService.validate(entityType, { demande_id: demandeId });
        break;
      case 'reject':
        obs$ = this.demandesService.reject(entityType, { demande_id: demandeId, motif });
        break;
      case 'cancel':
        obs$ = this.demandesService.cancel(entityType, { demande_id: demandeId });
        break;
      case 'club-accept':
        obs$ = this.api.post(API.DEMANDES.TRANSFER_CLUB_ACCEPT, { demande_id: demandeId });
        break;
      case 'club-reject':
        obs$ = this.api.post(API.DEMANDES.TRANSFER_CLUB_REJECT, { demande_id: demandeId, motif });
        break;
      default:
        this.isActing.set(false);
        return;
    }

    obs$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        if (res.success) {
          SwalUtils.success('Succès', 'Action effectuée avec succès');
          this.cancelAction();
          this.loadDemande();
        } else {
          SwalUtils.error('Erreur', res.message || 'Erreur lors de l\'action');
        }
        this.isActing.set(false);
      },
      error: () => {
        SwalUtils.error('Erreur', 'Erreur lors de l\'action');
        this.isActing.set(false);
      },
    });
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'PENDING':           return 'bg-yellow-100 text-yellow-800';
      case 'CLUB_ACCEPTED':     return 'bg-blue-100 text-blue-800';
      case 'FED_VALIDATED':     return 'bg-green-100 text-green-800';
      case 'FED_REJECTED':      return 'bg-red-100 text-red-800';
      case 'REJECTED_CLUB':     return 'bg-red-100 text-red-800';
      case 'CANCELLED':         return 'bg-stone-100 text-stone-600';
      case 'LICENCE_GENERATED': return 'bg-emerald-100 text-emerald-800';
      default:                  return 'bg-stone-100 text-stone-600';
    }
  }

  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      PENDING:           'En attente',
      CLUB_ACCEPTED:     'Accepté par le club',
      FED_VALIDATED:     'Validé par la fédération',
      FED_REJECTED:      'Rejeté par la fédération',
      REJECTED_CLUB:     'Refusé par le club',
      CANCELLED:         'Annulé',
      LICENCE_GENERATED: 'Licence générée',
    };
    return labels[status] || status;
  }

  goBack(): void {
    this.router.navigate(['/Demandes']);
  }
}
